import { Box } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { useId, useLayoutEffect, useRef, type ReactNode } from "react";

type FoodsSelectTriggerAdapterProps = {
    id?: string;
    labelId?: string;
    accessibleLabel?: string;
    descriptionId?: string;
    invalid?: boolean;
    children: ReactNode;
};

/**
 * FOODS 2.20.2 does not expose props for its select-variant button. Keep this bridge scoped to that exact rendered
 * contract until FOODS forwards trigger HTML props.
 */
export const FoodsSelectTriggerAdapter = ({
    id,
    labelId,
    accessibleLabel,
    descriptionId,
    invalid = false,
    children,
}: FoodsSelectTriggerAdapterProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const generatedId = useId();
    const triggerId = id ?? `foods-select-trigger-${generatedId}`;
    const hiddenLabelId = `foods-select-label-${generatedId}`;
    const resolvedLabelId = labelId ?? (accessibleLabel ? hiddenLabelId : undefined);

    useLayoutEffect(() => {
        const trigger = containerRef.current?.querySelector<HTMLButtonElement>("button.MuiButton-select");
        if (!trigger) return;

        trigger.id = triggerId;
        if (resolvedLabelId) trigger.setAttribute("aria-labelledby", `${resolvedLabelId} ${triggerId}`);
        else trigger.removeAttribute("aria-labelledby");

        if (descriptionId) trigger.setAttribute("aria-describedby", descriptionId);
        else trigger.removeAttribute("aria-describedby");

        if (invalid) trigger.setAttribute("aria-invalid", "true");
        else trigger.removeAttribute("aria-invalid");
    });

    return (
        <Box ref={containerRef} sx={{ display: "contents" }}>
            {accessibleLabel && labelId === undefined && (
                <Box component="span" id={hiddenLabelId} sx={visuallyHidden}>
                    {accessibleLabel}
                </Box>
            )}
            {children}
        </Box>
    );
};
